import {defineDimension} from '@webflow-platform/abilities';

export type IsReadOnlyConfig = {isReadOnly: boolean};
// The dimension reads from `ConstantsStore.readOnly` — a static slice mirrored
// from `DesignerMode.READ_ONLY` at flux construction. Both stores are optional
// so partial-state callers (including the legacy Designer abilities sub-selector,
// whose `stores: 'DesignerStore'` shape only carries `DesignerStore`) satisfy
// the type. `DesignerStore` is listed alongside `ConstantsStore` — even though
// the dimension does not read it — so that a `Pick<DesignerStores, 'DesignerStore'>`
// state shape (from the legacy sub-selector) has a property in common with this
// type and TypeScript accepts the assignment.
export type IsReadOnlyStores = {
  ConstantsStore?: {readOnly?: boolean};
  DesignerStore?: unknown;
};

export const isReadOnlyDimension = defineDimension<
  IsReadOnlyConfig,
  IsReadOnlyStores
>()({
  key: 'isReadOnly',
  isValid(state, config) {
    // Read from `ConstantsStore.readOnly` — a static slice mirrored from
    // `DesignerMode.READ_ONLY` at flux construction — instead of the
    // environment module, so the agnostic abilities system stays free of
    // environment reads. Use `in` before reading: partial store proxies
    // in the parity harness throw on missing keys even with `?.`. Missing
    // `ConstantsStore` or `readOnly` defaults to `false`, mirroring the
    // production initial state.
    const readOnly =
      'ConstantsStore' in state
        ? (state.ConstantsStore?.readOnly ?? false)
        : false;
    return readOnly === config.isReadOnly;
  },
  fragments: {
    whenReadOnly: () => ({isReadOnly: true}) as const,
    whenEditable: () => ({isReadOnly: false}) as const,
  },
  testMatrix(addAxis) {
    addAxis((addCase) => {
      addCase({
        label: 'isReadOnly',
        state: {ConstantsStore: {readOnly: true}},
      });
      addCase({
        label: '!isReadOnly',
        state: {ConstantsStore: {readOnly: false}},
      });
    });
  },
});

export const {whenReadOnly, whenEditable} = isReadOnlyDimension.fragments;
